// ThinkingIndicator.jsx — E2 animated working indicator (cycling verbs + a11y).
//
// The #6 "no working indicator" defect was zero CSS on .st-dot/.st-msg--thinking (fixed in the
// E1 SCSS slice) PLUS the indicator being a static block. This component adds:
//   • a cycling reassurance verb (~2.2s) so a long wait reads as progress, not a hang;
//   • screen-reader announcement via role="status" + aria-live="polite";
//   • three pulsing dots (.st-dot, animated by the E1 SCSS) + a label (.st-thinking__label).
//
// Reduced-motion: when `(prefers-reduced-motion: reduce)` matches we HOLD a single verb (no
// setInterval cycling) — pairs with the SCSS @media guard that freezes the dot pulse.
//
// The interval is cleared on unmount (useEffect cleanup) so it never leaks once the first token
// arrives and the indicator unmounts.
//
// NOTE: this is the ONE .jsx in this slice set. `node --test` has no JSX transform, so it is
// NEVER imported by a test — it's covered by a fs.readFileSync source-guard
// (__tests__/thinkingIndicator.test.mjs). The integrator wires <ThinkingIndicator/> into
// AgentStudioChat.jsx; the .st-dot/.st-thinking__label CSS is the E1 SCSS slice.

import React, { useEffect, useState } from "react"

export const VERBS = [
  "Working on that now",
  "Thinking it through",
  "Pulling things together",
  "Almost there",
]

const CYCLE_MS = 2200

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

// Item A — `mode="awaiting"` renders the PAUSED-for-human affordance: a calm, STATIC directive
// line ("Your turn — review above" / "Your turn — reply above"), no verb cycle, no dot pulse.
// Default (mode unset) is byte-identical to the original cycling "thinking" indicator.
export default function ThinkingIndicator({ mode = "thinking", label } = {}) {
  const [verbIndex, setVerbIndex] = useState(0)

  useEffect(() => {
    // Item A: the awaiting state is static — never cycle.
    if (mode === "awaiting") return undefined
    // Honor reduced motion: hold a single verb, do not cycle.
    if (prefersReducedMotion()) return undefined

    const id = setInterval(() => {
      setVerbIndex((i) => (i + 1) % VERBS.length)
    }, CYCLE_MS)

    return () => clearInterval(id)
  }, [mode])

  if (mode === "awaiting") {
    return (
      <div className="st-msg st-msg--awaiting" role="status" aria-live="polite">
        <span className="st-awaiting__dot" aria-hidden="true" />
        <span className="st-awaiting__label">{label || "Your turn — review above"}</span>
      </div>
    )
  }

  const verb = VERBS[verbIndex]

  return (
    <div className="st-msg st-msg--thinking" role="status" aria-live="polite">
      <span className="st-dot" />
      <span className="st-dot" />
      <span className="st-dot" />
      <span className="st-thinking__label">{verb}</span>
    </div>
  )
}
